import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

type MarkdownContentProps = {
  content: string;
};

export type MarkdownHeading = {
  id: string;
  level: 2 | 3;
  text: string;
};

const IMAGE_WIDTH_RE = /!\[([^\]]*)\]\(([^)\s]+)\)\{width=([1-9][0-9]?|100)%\}/g;
const FENCE_RE = /^\s*(```|~~~)/;

const slugifyHeading = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[`*_~[\]()]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '') || 'section';

const stripMarkdownInline = (text: string): string =>
  text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_~]/g, '')
    .trim();

export const extractMarkdownHeadings = (content: string): MarkdownHeading[] => {
  const headings: MarkdownHeading[] = [];
  let inFence = false;
  content.split('\n').forEach((line) => {
    if (FENCE_RE.test(line)) {
      inFence = !inFence;
      return;
    }
    if (inFence) return;
    const match = line.match(/^(#{2,3})\s+(.+)$/);
    if (!match) return;
    const text = stripMarkdownInline(match[2]);
    headings.push({
      id: slugifyHeading(text),
      level: match[1].length as 2 | 3,
      text,
    });
  });
  return headings;
};

const encodeImageWidth = (content: string): string => {
  let inFence = false;
  return content
    .split('\n')
    .map((line) => {
      if (FENCE_RE.test(line)) {
        inFence = !inFence;
        return line;
      }
      if (inFence) return line;
      return line.replace(IMAGE_WIDTH_RE, (_match, alt: string, src: string, width: string) => {
        const marker = src.includes('#') ? '&gib-width=' : '#gib-width=';
        return `![${alt}](${src}${marker}${width})`;
      });
    })
    .join('\n');
};

const decodeImageWidth = (src?: string): { src?: string; width?: string } => {
  if (!src) return {};
  const match = src.match(/([#&])gib-width=([1-9][0-9]?|100)$/);
  if (!match) return { src };
  return {
    src: src.slice(0, match.index),
    width: `${match[2]}%`,
  };
};

const MarkdownContent = ({ content }: MarkdownContentProps) => (
  <div className="helpMarkdown">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize]}
      components={{
        h2: ({ node: _node, children, ...props }) => {
          const text = stripMarkdownInline(String(children));
          return (
            <h2 id={slugifyHeading(text)} {...props}>
              {children}
            </h2>
          );
        },
        h3: ({ node: _node, children, ...props }) => {
          const text = stripMarkdownInline(String(children));
          return (
            <h3 id={slugifyHeading(text)} {...props}>
              {children}
            </h3>
          );
        },
        img: ({ node: _node, src, alt, style, ...props }) => {
          const decoded = decodeImageWidth(src);
          return <img alt={alt || ''} src={decoded.src} style={{ ...style, width: decoded.width }} {...props} />;
        },
      }}
    >
      {encodeImageWidth(content)}
    </ReactMarkdown>
  </div>
);

export default MarkdownContent;
